import React from "react";

import { Check, Copy, FileText, MoreVertical } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useToast } from "~/hooks/use-toast";
import { SalesRequest } from "~/types/client";
import { formatDate } from "~/utils/helpers";

interface OrderDetailHeaderProps {
  selectedOrder: SalesRequest;
  isCopied: boolean;
  setIsCopied: (isCopied: boolean) => void;
  canCreateDispatchGuide?: boolean;
  onCreateDispatchGuide?: () => void;
  isCreatingDispatchGuide?: boolean;
}

export const OrderDetailHeader: React.FC<OrderDetailHeaderProps> = ({
  selectedOrder,
  isCopied,
  setIsCopied,
  canCreateDispatchGuide = false,
  onCreateDispatchGuide,
  isCreatingDispatchGuide = false,
}) => {
  const { toast } = useToast();

  // Format the date for display
  const formattedDate = formatDate(selectedOrder.createdAt);

  // Handler to copy the sales request ID
  const copyToClipboard = () => {
    navigator.clipboard.writeText(selectedOrder.id);
    setIsCopied(true);

    toast({
      title: "ID copiado al portapapeles",
      description: `${selectedOrder.id}`,
    });

    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  return (
    <div className="mb-2 border-b pb-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <h2 className="text-xl font-bold tracking-tight text-gray-900">
            Detalles de Nota de Venta
          </h2>

          <div className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1">
            <code className="font-mono text-xs text-gray-700">
              {selectedOrder.id.substring(0, 8)}
            </code>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full hover:bg-gray-200"
              onClick={copyToClipboard}
            >
              {isCopied ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 text-gray-500" />
              )}
              <span className="sr-only">Copiar ID</span>
            </Button>
          </div>

          <span className="text-sm text-muted-foreground">{formattedDate}</span>
        </div>

        {/* Ellipsis Menu - Only show if there are actions available */}
        {canCreateDispatchGuide && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Abrir menú</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={onCreateDispatchGuide}
                disabled={isCreatingDispatchGuide}
                className="cursor-pointer"
              >
                <FileText className="mr-2 h-4 w-4" />
                {isCreatingDispatchGuide
                  ? "Creando Guía..."
                  : "Crear Guía de Despacho"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
